import * as React from 'react'

import { ChevronDown, ChevronUp } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import Localization from '~/client/src/shared/localization/LocalizationManager'

// localization: translated

// Closure and outside-operating-hours warnings tend to pile up on recurring
// workflows or resources with many windows. Beyond this many in a group, they
// collapse to a single visible entry with a show-more / show-less toggle.
// Other reasons (reserved-for, max booking duration, overlap) are never
// collapsed.
const COLLAPSE_ABOVE = 2

interface IProps {
  messages: string[]
  // Per-message styling — supplied by the surface so the field (legacy helper
  // text) and the picker card (Tailwind) each keep their own look.
  messageClassName: string
  // Container styling (padding/spacing). Lives on the wrapper so the toggle
  // lines up with the messages regardless of the surface's padding scheme.
  className?: string
}

interface ICollapsibleGroupProps {
  messages: string[]
  messageClassName: string
  moreLabel: string
  lessLabel: string
}

// A self-collapsing group of same-kind messages: shows one entry with a
// show-more / show-less toggle once the group exceeds COLLAPSE_ABOVE. Each group
// owns its own expand state so closures and operating hours toggle independently.
const CollapsibleMessageGroup: React.FC<ICollapsibleGroupProps> = ({
  messages,
  messageClassName,
  moreLabel,
  lessLabel,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false)

  if (!messages.length) return null

  const isCollapsible = messages.length > COLLAPSE_ABOVE
  const visible = isCollapsible && !isExpanded ? messages.slice(0, 1) : messages

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsExpanded(prev => !prev)
  }

  return (
    <>
      {visible.map((message, index) => (
        <div key={index} className={messageClassName}>
          {message}
        </div>
      ))}
      {isCollapsible && (
        <Button
          variant="ghost"
          size="xs"
          onClick={toggle}
          // Negative left margin cancels the ghost button's own left padding so
          // its label lines up with the messages above, while the hover surface
          // still extends past the text.
          className="tw:-ml-2.5 tw:text-body-sm tw:font-light tw:text-muted-foreground"
        >
          {isExpanded ? lessLabel : moreLabel}
          {isExpanded ? <ChevronUp /> : <ChevronDown />}
        </Button>
      )}
    </>
  )
}

const RestrictionMessages: React.FC<IProps> = ({
  messages,
  messageClassName,
  className,
}) => {
  if (!messages.length) return null

  // Groups collapse independently and render after the never-collapsed others,
  // in this order: closures, then operating hours.
  const groups = [
    {
      prefix: `${Localization.translator.closed}:`,
      moreLabel: 'Show more closures',
      lessLabel: 'Show less closures',
    },
    {
      prefix: `${Localization.translator.outsideOperatingHours}:`,
      moreLabel: 'Show more operating hours',
      lessLabel: 'Show less operating hours',
    },
  ]

  const others = messages.filter(
    message => !groups.some(group => message.startsWith(group.prefix)),
  )

  return (
    <div className={className}>
      {others.map((message, index) => (
        <div key={index} className={messageClassName}>
          {message}
        </div>
      ))}
      {groups.map(group => (
        <CollapsibleMessageGroup
          key={group.prefix}
          messages={messages.filter(message =>
            message.startsWith(group.prefix),
          )}
          messageClassName={messageClassName}
          moreLabel={group.moreLabel}
          lessLabel={group.lessLabel}
        />
      ))}
    </div>
  )
}

export default RestrictionMessages
